import React, { useState, useEffect } from 'react';
import { resolveMediaUrl } from '../utils/mediaHelper';

interface RobustImageProps {
  key?: string;
  mediaKey: string;
  localPaths: string[];
  fallbackUnsplashUrl: string;
  alt: string;
  className?: string;
  width?: string | number;
  height?: string | number;
  loading?: 'lazy' | 'eager';
}

/**
 * RobustImage handles local images that will be added via GitHub.
 * It systematically tries local paths, and if those fail to load (error or not found),
 * it falls back to subsequent paths, and finally to a beautiful Unsplash fallback URL.
 */
export default function RobustImage({
  mediaKey,
  localPaths,
  fallbackUnsplashUrl,
  alt,
  className,
  width,
  height,
  loading = 'lazy'
}: RobustImageProps) {
  const [attemptIndex, setAttemptIndex] = useState(0);
  const [src, setSrc] = useState(() => {
    return localPaths.length > 0 
      ? resolveMediaUrl(localPaths[0], mediaKey)
      : resolveMediaUrl(fallbackUnsplashUrl, mediaKey);
  });

  // Generate the full list of candidate URLs
  const candidateUrls = React.useMemo(() => {
    const urls: string[] = [];
    
    // Add all specified local paths
    localPaths.forEach(path => {
      // First try resolving via media key in case the user customized it in local storage
      urls.push(resolveMediaUrl(path, mediaKey));
    });

    // Add fallback Unsplash URL
    urls.push(resolveMediaUrl(fallbackUnsplashUrl, mediaKey));

    return urls;
  }, [localPaths, fallbackUnsplashUrl, mediaKey]);

  useEffect(() => {
    setAttemptIndex(0);
    if (candidateUrls.length > 0) {
      setSrc(candidateUrls[0]);
    }
  }, [candidateUrls]);

  const handleError = () => {
    if (attemptIndex + 1 < candidateUrls.length) {
      const nextIndex = attemptIndex + 1;
      setAttemptIndex(nextIndex);
      setSrc(candidateUrls[nextIndex]);
    }
  };

  // Determine dynamic alt text for SEO/AEO optimization
  let activeAlt = alt;
  if (src.includes('cozzydine_logo_about') || src.includes('cozzydine_logo_about%20us')) {
    activeAlt = 'Cozzydine logo featuring the brand name, icon and slogan.';
  } else if (src.includes('cozzydine_logo.png')) {
    activeAlt = 'Cozzydine logo featuring the brand name, icon and slogan.';
  }

  return (
    <img
      src={src}
      onError={handleError}
      alt={activeAlt}
      className={className}
      width={width}
      height={height}
      referrerPolicy="no-referrer"
      loading={loading}
      decoding="async"
    />
  );
}
